// Tenant Home — /tenant
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TenantShell from "@/components/TenantShell";
import { Button } from "@/components/ui/button";
import {
  Building2,
  CalendarCheck,
  CreditCard,
  MessageSquare,
  Wrench,
  Loader2,
  Mail,
} from "lucide-react";

function Pill({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-700",
    ending_soon: "bg-amber-100 text-amber-700",
    expired: "bg-slate-100 text-slate-500",
    terminated: "bg-red-100 text-red-700",
    paid: "bg-emerald-100 text-emerald-700",
    scheduled: "bg-slate-100 text-slate-600",
    late: "bg-red-100 text-red-700",
    missed: "bg-red-100 text-red-700",
    refunded: "bg-slate-100 text-slate-600",
  };
  const label = (status || "").replace("_", " ");
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {label}
    </span>
  );
}

export default function TenantHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/tenant/home")
      .then(({ data }) => setData(data))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <TenantShell>
        <div className="flex h-64 items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </TenantShell>
    );
  }

  const { property, lease, lease_status, lease_days_remaining, next_rent_due, open_maintenance_count, recent_payments, landlord } = data || {};

  return (
    <TenantShell>
      <div className="space-y-7" data-testid="tenant-home">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Tenant portal</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Welcome home
          </h1>
          <p className="mt-1 text-sm text-slate-500">Everything about your place — lease, maintenance, payments, and your landlord.</p>
        </header>

        {!property && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-8 text-center" data-testid="tenant-home-empty">
            <p className="text-sm text-slate-500">No active lease on file. If you've recently been invited, ask your landlord for a fresh invite link.</p>
          </div>
        )}

        {property && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* My Place */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tenant-card-place">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-600" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">My place</p>
              </div>
              <p className="mt-3 text-base font-semibold text-slate-900">{property.address}</p>
              <p className="text-sm text-slate-500">{property.city}, {property.state || property.country}</p>
              {property.beds || property.baths ? (
                <p className="mt-2 text-xs text-slate-500">
                  {property.beds || 0} bed · {property.baths || 0} bath · {property.square_feet || 0} sqft
                </p>
              ) : null}
            </div>

            {/* Lease status */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tenant-card-lease">
              <div className="flex items-center gap-2">
                <CalendarCheck className="h-4 w-4 text-indigo-600" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Lease</p>
              </div>
              {lease ? (
                <>
                  <div className="mt-3 flex items-center gap-2">
                    <Pill status={lease_status} />
                    {lease_days_remaining !== null && lease_days_remaining !== undefined && (
                      <span className="text-xs text-slate-500">{lease_days_remaining}d remaining</span>
                    )}
                  </div>
                  <p className="mt-2 text-base font-semibold tabular-nums text-slate-900">
                    {lease.monthly_rent} {lease.currency}/mo
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Next rent due: {next_rent_due}</p>
                  <Button variant="ghost" size="sm" className="mt-3 -ml-2 text-indigo-600" onClick={() => navigate("/tenant/lease")} data-testid="tenant-view-lease">
                    View full lease →
                  </Button>
                </>
              ) : (
                <p className="mt-3 text-sm text-slate-400">No active lease yet.</p>
              )}
            </div>

            {/* Maintenance */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tenant-card-maintenance">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-indigo-600" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Maintenance</p>
              </div>
              <p className="mt-3 text-3xl font-bold tabular-nums text-slate-900">{open_maintenance_count || 0}</p>
              <p className="mt-1 text-xs text-slate-500">open work order{(open_maintenance_count || 0) === 1 ? "" : "s"}</p>
              <Button
                className="mt-3 bg-indigo-600 text-white hover:bg-indigo-700"
                size="sm"
                onClick={() => navigate("/tenant/maintenance")}
                data-testid="tenant-report-issue"
              >
                Report an issue
              </Button>
            </div>

            {/* Recent payments */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tenant-card-payments">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-indigo-600" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Recent payments</p>
              </div>
              {(recent_payments || []).length === 0 ? (
                <p className="mt-3 text-sm text-slate-400">No payments yet.</p>
              ) : (
                <ul className="mt-3 space-y-1.5">
                  {recent_payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between text-xs" data-testid={`tenant-payment-row-${p.id}`}>
                      <span className="text-slate-600">{p.due_date}</span>
                      <span className="font-medium tabular-nums text-slate-900">{p.amount} {p.currency}</span>
                      <Pill status={p.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Landlord contact */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:col-span-2 lg:col-span-1" data-testid="tenant-card-landlord">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-indigo-600" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Your landlord</p>
              </div>
              {landlord ? (
                <>
                  <p className="mt-3 text-base font-semibold text-slate-900">{landlord.name}</p>
                  <p className="text-xs text-slate-500">{landlord.email}</p>
                  <Button
                    variant="outline"
                    className="mt-3 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    size="sm"
                    onClick={() => navigate("/tenant/maintenance?msg=1")}
                    data-testid="tenant-message-landlord"
                  >
                    <MessageSquare className="mr-2 h-3 w-3" /> Message landlord
                  </Button>
                </>
              ) : (
                <p className="mt-3 text-sm text-slate-400">Landlord info unavailable.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </TenantShell>
  );
}
